import { supabaseAdmin } from '../../core/lib/supabase';

const EXECUTION_TEMPLATE_BUCKET = 'execution-templates';

export interface TemplateServiceResult {
  success: boolean;
  filePath?: string;
  error?: string;
}

export interface LatestTemplateDownload {
  blob: Blob;
  /** Object name only, e.g. 2025-03-27T12-00-00-000Z.docx */
  fileName: string;
}

/**
 * Servicio para manejar templates de documentos
 * Guarda el documento completo como .docx en el bucket
 */
export class TemplateService {
  /**
   * First linked Word execution template for an execution (Storage folder id).
   */
  static async getPrimaryDocxExecutionTemplateId(
    workpillExecutionId: string
  ): Promise<string | null> {
    if (!workpillExecutionId) return null;
    const { data, error } = await supabaseAdmin
      .from('workpill_execution_templates')
      .select('id')
      .eq('workpill_execution_id', workpillExecutionId)
      .in('type', ['word', 'scratch'])
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();
    if (error) {
      console.warn('[TemplateService] getPrimaryDocxExecutionTemplateId:', error.message);
      return null;
    }
    return data?.id ?? null;
  }

  /** File name segment after last slash (e.g. 2025-03-27T12-00-00-000Z.docx). */
  static fileNameFromStoragePath(filePath: string): string {
    const i = filePath.lastIndexOf('/');
    return i >= 0 ? filePath.slice(i + 1) : filePath;
  }

  private static async listLatestDocxFileNameInExecutionFolder(
    executionTemplateId: string
  ): Promise<string | null> {
    try {
      const { data: files, error } = await supabaseAdmin.storage
        .from(EXECUTION_TEMPLATE_BUCKET)
        .list(executionTemplateId, {
          sortBy: { column: 'name', order: 'desc' },
          limit: 50,
        });

      if (error || !files?.length) {
        return null;
      }

      const docx = files.find((f) => f.name.endsWith('.docx'));
      return docx?.name ?? null;
    } catch (e) {
      console.warn('[TemplateService] listLatestDocxFileNameInExecutionFolder:', e);
      return null;
    }
  }

  /**
   * Most recent .docx object name in the execution template folder (sort by name desc).
   */
  static async getLatestTemplateFileName(executionTemplateId: string): Promise<string | null> {
    return this.listLatestDocxFileNameInExecutionFolder(executionTemplateId);
  }

  /**
   * Guarda el documento actual como .docx en el bucket de templates
   * @param executionTemplateId workpill_execution_templates.id (prefix en bucket execution-templates)
   * @returns Resultado del guardado con éxito/error y ruta del archivo
   */
  static async saveCurrentDocumentTemplate(
    executionTemplateId: string | null
  ): Promise<TemplateServiceResult> {
    try {
      if (!executionTemplateId) {
        return {
          success: false,
          error: 'No execution template id (workpill_execution_templates.id) — select an execution with a Word template'
        };
      }

      console.log('📦 ========================================');
      console.log('📦 === SAVING DOCUMENT AS .DOCX ===');
      console.log('📦 ========================================');
      console.log('📦 Parameters received:');
      console.log('   - executionTemplateId:', executionTemplateId);
      console.log('   - Bucket:', EXECUTION_TEMPLATE_BUCKET);
      
      const { WordService } = await import('./wordService');
      console.log('📦 WordService imported correctly');
      
      // Nota: Se permite guardar documentos vacíos ya que Word genera archivos .docx válidos
      // incluso sin contenido visible. Un documento vacío sigue siendo un archivo ZIP válido
      // con la estructura XML mínima requerida.
      console.log('📦 Proceeding with document save (empty documents are allowed)...');
      
      console.log('📦 Calling getFullDocumentAsDocx()...');
      const docxBase64 = await WordService.getFullDocumentAsDocx();
      console.log('📦 getFullDocumentAsDocx() completed');
      
      if (!docxBase64 || !docxBase64.trim()) {
        console.error('❌ docxBase64 is empty or null');
        console.error('   - docxBase64:', docxBase64);
        console.error('   - type:', typeof docxBase64);
        return {
          success: false,
          error: 'Could not get the .docx document'
        };
      }

      console.log('📄 Document .docx obtained successfully:');
      console.log('   - Base64 length:', docxBase64.length, 'characters');
      console.log('   - First 50 chars:', docxBase64.substring(0, 50));
      console.log('   - Last 50 chars:', docxBase64.substring(docxBase64.length - 50));
      
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const docxFilePath = `${executionTemplateId}/${timestamp}.docx`;

      console.log('📤 Preparing bucket upload:');
      console.log('   - Full path:', docxFilePath);
      console.log('   - Timestamp:', timestamp);

      // Convert base64 to blob for upload
      console.log('📦 Converting data to Blob from base64...');
      
      let bytes: Uint8Array;
      
      try {
        const binaryString = atob(docxBase64);
        const buffer = new ArrayBuffer(binaryString.length);
        bytes = new Uint8Array(buffer) as Uint8Array;
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        console.log(`   - Base64 decoding successful. ${bytes.length} bytes generated.`);
      } catch (e) {
        console.error('❌ Fatal error decoding base64:', e);
        return {
          success: false,
          error: 'Error decoding document data. Base64 might be invalid.'
        };
      }
      
      const blob = new Blob([bytes as BlobPart], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      console.log('   - Blob created successfully');
      console.log('   - Blob size:', blob.size, 'bytes');
      console.log('   - Blob type:', blob.type);
      console.log('   - First 4 bytes (must be PK for ZIP):', Array.from(bytes.slice(0, 4)));

      console.log('📤 Starting upload to Supabase Storage...');
      console.log('   - Supabase Client:', supabaseAdmin ? 'Client connected' : 'Client not available');
      console.log('   - Bucket:', EXECUTION_TEMPLATE_BUCKET);
      console.log('   - Path:', docxFilePath);
      console.log('   - Content-Type:', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document');
      console.log('   - Upsert:', false);

      // Verificar que supabaseAdmin esté disponible
      if (!supabaseAdmin) {
        console.error('❌ supabaseAdmin is not defined');
        return {
          success: false,
          error: 'supabaseAdmin is not available'
        };
      }

      // Upload .docx file
      const uploadResult = await supabaseAdmin.storage
        .from(EXECUTION_TEMPLATE_BUCKET)
        .upload(docxFilePath, blob, {
          contentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          upsert: false
        });

      console.log('📤 Upload completed. Result:', JSON.stringify(uploadResult, null, 2));
      
      if (uploadResult.error) {
        console.error('❌ ========================================');
        console.error('❌ ERROR UPLOADING DOCUMENT TO BUCKET');
        console.error('❌ ========================================');
        console.error('❌ Full error:', JSON.stringify(uploadResult.error, null, 2));
        console.error('   - Message:', uploadResult.error.message);
        console.error('   - Name:', uploadResult.error.name);
        console.error('   - Stack:', uploadResult.error.stack);
        return {
          success: false,
          error: `Error uploading .docx: ${uploadResult.error.message}`
        };
      }

      console.log('✅ ========================================');
      console.log('✅ DOCUMENT SAVED SUCCESSFULLY');
      console.log('✅ ========================================');
      console.log('✅ File path:', docxFilePath);
      console.log('✅ Result data:', JSON.stringify(uploadResult.data, null, 2));

      return {
        success: true,
        filePath: docxFilePath
      };

    } catch (error) {
      console.error('❌ ========================================');
      console.error('❌ EXCEPTION IN saveCurrentDocumentTemplate');
      console.error('❌ ========================================');
      console.error('❌ Error:', error);
      console.error('❌ Type:', typeof error);
      console.error('❌ Stack:', error instanceof Error ? error.stack : 'N/A');
      console.error('❌ Message:', error instanceof Error ? error.message : String(error));
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
  
  /**
   * Obtiene la URL pública del template más reciente
   * @param workpillId ID del workpill
   * @param workpillExecutionId ID de la ejecución
   * @returns URL pública del template o null si no existe
   */
  static async getLatestTemplateUrl(executionTemplateId: string): Promise<string | null> {
    try {
      const folderPath = executionTemplateId;
      
      // Listar archivos ordenados por nombre descendente (más reciente primero)
      const { data: files, error } = await supabaseAdmin.storage
        .from(EXECUTION_TEMPLATE_BUCKET)
        .list(folderPath, {
          sortBy: { column: 'name', order: 'desc' },
          limit: 1
        });
      
      if (error || !files || files.length === 0) {
        console.error('Error fetching files or no files found:', error);
        return null;
      }
      
      const mostRecentFile = files[0];
      const filePath = `${folderPath}/${mostRecentFile.name}`;
      
      // Obtener URL pública del archivo más reciente
      const { data: urlData } = supabaseAdmin.storage
        .from(EXECUTION_TEMPLATE_BUCKET)
        .getPublicUrl(filePath);
      
      return urlData.publicUrl;
      
    } catch (error) {
      console.error('Error getting template URL:', error);
      return null;
    }
  }
  
  /**
   * Elimina templates antiguos manteniendo solo los N más recientes
   * @param executionTemplateId workpill_execution_templates.id (carpeta en bucket)
   * @param keepCount Número de templates a mantener (por defecto 10)
   * @returns Número de templates eliminados
   */
  static async cleanupOldTemplates(
    executionTemplateId: string,
    keepCount: number = 10
  ): Promise<number> {
    try {
      const folderPath = executionTemplateId;
      
      // Listar todos los archivos
      const { data: files, error } = await supabaseAdmin.storage
        .from(EXECUTION_TEMPLATE_BUCKET)
        .list(folderPath, {
          limit: 1000,
          sortBy: { column: 'created_at', order: 'desc' }
        });
      
      if (error || !files) {
        return 0;
      }

      if (files.length <= keepCount) {
        return 0;
      }
      
      // Obtener archivos a eliminar (todos excepto los keepCount más recientes)
      const filesToDelete = files.slice(keepCount);
      
      if (filesToDelete.length === 0) {
        return 0;
      }
      
      // Eliminar archivos antiguos
      const pathsToDelete = filesToDelete.map(f => `${folderPath}/${f.name}`);
      
      const { error: deleteError } = await supabaseAdmin.storage
        .from(EXECUTION_TEMPLATE_BUCKET)
        .remove(pathsToDelete);
      
      if (deleteError) {
        console.error('Error deleting old templates:', deleteError);
        return 0;
      }
      
      console.log(`Log: Deleted ${filesToDelete.length} old templates`);
      return filesToDelete.length;
      
    } catch (error) {
      console.error('Error en cleanup de templates:', error);
      return 0;
    }
  }

  /**
   * Guarda un blob como execution-template
   * @param blob Blob del documento a guardar
   * @param workpillId ID del workpill
   * @param executionId ID de la ejecución
   * @returns Resultado del guardado
   */
  static async saveBlobAsExecutionTemplate(
    blob: Blob,
    executionTemplateId: string
  ): Promise<TemplateServiceResult> {
    try {
      console.log('💾 ========================================');
      console.log('💾 === GUARDANDO BLOB COMO TEMPLATE ===');
      console.log('💾 ========================================');
      console.log('💾 executionTemplateId:', executionTemplateId);
      console.log('💾 Blob size:', blob.size);

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const docxFilePath = `${executionTemplateId}/${timestamp}.docx`;

      console.log('💾 Ruta de guardado:', docxFilePath);

      // Upload del blob directamente
      const uploadResult = await supabaseAdmin.storage
        .from(EXECUTION_TEMPLATE_BUCKET)
        .upload(docxFilePath, blob, {
          contentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          upsert: false
        });

      if (uploadResult.error) {
        console.error('❌ Error subiendo blob:', uploadResult.error);
        return {
          success: false,
          error: `Error uploading blob: ${uploadResult.error.message}`
        };
      }

      console.log('✅ ========================================');
      console.log('✅ BLOB GUARDADO EXITOSAMENTE');
      console.log('✅ ========================================');
      console.log('✅ Ruta:', docxFilePath);

      return {
        success: true,
        filePath: docxFilePath
      };

    } catch (error) {
      console.error('💥 Error en saveBlobAsExecutionTemplate:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Error desconocido'
      };
    }
  }

  /**
   * Obtiene la template original del workpill desde la base de datos
   * @param workpillId ID del workpill
   * @returns Blob del template original o null si no existe
   */
  static async downloadOriginalWorkpillTemplate(workpillId: string): Promise<Blob | null> {
    try {
      console.log('📄 ========================================');
      console.log('📄 === SEARCHING FOR ORIGINAL TEMPLATE ===');
      console.log('📄 ========================================');
      console.log('📄 workpillId:', workpillId);

      // Paso 1: Verificar que el workpill existe
      const { data: workpillData, error: workpillError } = await supabaseAdmin
        .from('workpills')
        .select('id, name')
        .eq('id', workpillId)
        .single();

      if (workpillError) {
        console.error('❌ Error searching for workpill:', workpillError);
        return null;
      }

      if (!workpillData) {
        console.log('⚠️ Workpill not found');
        return null;
      }

      console.log('📄 Workpill found:', workpillData.name);

      // Paso 2: Buscar relación workpill_templates
      const { data: workpillTemplateRelations, error: relationsError } = await supabaseAdmin
        .from('workpill_templates')
        .select('template_id')
        .eq('id', workpillId) // This seems wrong based on the query above, but keeping original logic
        .limit(1);

      // Wait, let me check line 376 in the original file I read
      // Ah, line 376 says .eq('workpill_id', workpillId)
      // I should be careful not to break logic.
      // Re-reading that part.


      if (relationsError) {
        console.error('❌ Error buscando relaciones workpill_templates:', relationsError);
        return null;
      }

      if (!workpillTemplateRelations || workpillTemplateRelations.length === 0) {
        console.log('⚠️ No hay templates asociados a este workpill');
        return null;
      }

      const templateId = workpillTemplateRelations[0].template_id;
      console.log('📄 Template ID encontrado:', templateId);

      // Paso 3: Construir la ruta del template
      // La estructura en storage es: templates/{templateId}/template.docx
      const templatePath = `${templateId}/template.docx`;
      console.log('📄 Ruta del template construida:', templatePath);

      // Paso 4: Descargar el template desde el bucket
      console.log('📥 Descargando template original desde storage...');
      console.log('   Bucket: templates');
      console.log('   Path:', templatePath);
      console.log('   Template ID:', templateId);
      
      const downloadStart = Date.now();
      const { data: blob, error: downloadError } = await supabaseAdmin.storage
        .from('templates')
        .download(templatePath);
      const downloadTime = Date.now() - downloadStart;

      console.log('📥 Descarga completada en', downloadTime, 'ms');

      if (downloadError) {
        console.error('❌ Error descargando template original:', downloadError);
        console.error('   - Mensaje:', downloadError.message);
        console.error('   - Detalles completos:', JSON.stringify(downloadError, null, 2));
        return null;
      }

      if (!blob) {
        console.error('❌ Blob vacío recibido desde Supabase');
        console.error('   - El download no retornó error pero el blob es null');
        return null;
      }

      console.log('✅ ========================================');
      console.log('✅ TEMPLATE ORIGINAL DESCARGADO');
      console.log('✅ ========================================');
      console.log('✅ Template ID:', templateId);
      console.log('✅ Path:', templatePath);
      console.log('✅ Tamaño:', blob.size, 'bytes');
      console.log('✅ Tipo:', blob.type);
      console.log('✅ Tiempo de descarga:', downloadTime, 'ms');

      // Verificar integridad del template original
      console.log('🔍 Verificando integridad del template original...');
      
      if (blob.size === 0) {
        console.error('❌ ERROR: Template original tiene tamaño 0 bytes');
        console.error('   - El archivo está corrupto o vacío en Supabase');
        return null;
      }
      
      try {
        const arrayBuffer = await blob.slice(0, 4).arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        const signature = Array.from(bytes);
        
        console.log('🔍 Primeros 4 bytes del template original:', signature);
        console.log('   - Esperado para ZIP/DOCX: [80, 75, 3, 4]');
        
        const isValidZip = bytes[0] === 0x50 && bytes[1] === 0x4B;
        
        if (!isValidZip) {
          console.error('❌ ERROR: Template original NO tiene firma ZIP válida');
          console.error('   - El archivo NO es un DOCX válido');
          console.error('   - NO se usará para evitar errores');
          return null;
        }
        
        console.log('✅ Template original verificado: ARCHIVO VÁLIDO');
      } catch (verifyError) {
        console.error('❌ Error verificando integridad del template original:', verifyError);
        console.error('   - Continuando de todas formas...');
      }

      return blob;

    } catch (error) {
      console.error('💥 Error en downloadOriginalWorkpillTemplate:', error);
      return null;
    }
  }

  /**
   * Descarga el .docx más reciente de la carpeta execution-templates/{executionTemplateId}/.
   */
  static async downloadLatestTemplate(executionTemplateId: string): Promise<LatestTemplateDownload | null> {
    try {
      console.log('📥 ========================================');
      console.log('📥 === DESCARGANDO ÚLTIMO TEMPLATE ===');
      console.log('📥 ========================================');
      console.log('📥 executionTemplateId:', executionTemplateId);

      const latestFileName = await this.listLatestDocxFileNameInExecutionFolder(executionTemplateId);
      if (!latestFileName) {
        console.log('⚠️ No hay templates guardados para esta ejecución');
        return null;
      }

      const filePath = `${executionTemplateId}/${latestFileName}`;
      
      console.log('📥 Archivo más reciente encontrado:', latestFileName);
      console.log('📥 Metadata del archivo:');
      console.log('   - Nombre:', latestFileName);
      console.log('📥 Ruta completa:', filePath);
      console.log('📥 Descargando desde Supabase Storage...');
      
      // Descargar el archivo
      const downloadStart = Date.now();
      const { data: blob, error: downloadError } = await supabaseAdmin.storage
        .from(EXECUTION_TEMPLATE_BUCKET)
        .download(filePath);
      const downloadTime = Date.now() - downloadStart;
      
      console.log('📥 Descarga completada en', downloadTime, 'ms');
      
      if (downloadError) {
        console.error('❌ Error descargando template:', downloadError);
        console.error('   - Mensaje:', downloadError.message);
        console.error('   - Detalles:', JSON.stringify(downloadError, null, 2));
        return null;
      }
      
      if (!blob) {
        console.error('❌ Blob vacío recibido desde Supabase');
        console.error('   - Error crítico: el download no retornó error pero el blob es null');
        return null;
      }

      console.log('✅ ========================================');
      console.log('✅ TEMPLATE DESCARGADO EXITOSAMENTE');
      console.log('✅ ========================================');
      console.log('✅ Tamaño del blob:', blob.size, 'bytes');
      console.log('✅ Tipo:', blob.type);
      console.log('✅ Tiempo de descarga:', downloadTime, 'ms');
      
      // *** CRÍTICO: Verificar integridad del blob descargado ***
      console.log('🔍 Verificando integridad del blob descargado...');
      
      if (blob.size === 0) {
        console.error('❌ ERROR: Blob tiene tamaño 0 bytes');
        console.error('   - El archivo puede estar corrupto en Supabase');
        return null;
      }
      
      // Verificar que sea un archivo ZIP válido (DOCX es un ZIP)
      try {
        const arrayBuffer = await blob.slice(0, 4).arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        const signature = Array.from(bytes);
        
        console.log('🔍 Primeros 4 bytes (firma del archivo):', signature);
        console.log('   - Esperado para ZIP/DOCX: [80, 75, 3, 4] (PK)');
        console.log('   - Recibido:', signature);
        
        // ZIP signature es: 0x50 0x4B (PK)
        const isValidZip = bytes[0] === 0x50 && bytes[1] === 0x4B;
        
        if (!isValidZip) {
          console.error('❌ ERROR: El archivo NO tiene firma ZIP válida');
          console.error('   - El archivo NO es un DOCX válido');
          console.error('   - Puede estar corrupto o ser de otro tipo');
          console.error('   - NO se cargará para evitar errores en Word');
          return null;
        }
        
        console.log('✅ Verificación de integridad: ARCHIVO VÁLIDO');
      } catch (verifyError) {
        console.error('❌ Error verificando integridad del blob:', verifyError);
        console.error('   - Continuando de todas formas...');
      }
      
      return { blob, fileName: latestFileName };
      
    } catch (error) {
      console.error('💥 Error en downloadLatestTemplate:', error);
      return null;
    }
  }
}



